import apiV2 from '../api/v2';
import type { PaginatedMeta, SortOrder } from './accounts';

// Accounts > Orthodontists. Backed by GET /api/v2/orthodontist (admin-only) -
// the parallel list to GET /practices, one org row per orthodontist group.
export interface OrthodontistListItem {
    id: string;
    name: string;
    drFirstName: string;
    drLastName: string;
    city: string | null;
    state: string | null;
    patientCount: number;
    practiceCount: number;
    createdAt: string;
}

export type OrthodontistsSortBy = 'name' | 'createdAt' | 'patientCount' | 'practiceCount';

export interface OrthodontistsListParams {
    page: number;
    limit: number;
    sort?: OrthodontistsSortBy;
    order?: SortOrder;
    /** Searches org name/drFirstName/drLastName (ILIKE). */
    q?: string;
    /** Exact match against orthodontist.state. */
    state?: string;
}

export const fetchOrthodontists = async (
    params: OrthodontistsListParams
): Promise<{ data: OrthodontistListItem[]; meta: PaginatedMeta }> => {
    const query: Record<string, string> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;
    if (params.q) query.q = params.q;
    if (params.state) query.state = params.state;

    const response = await apiV2.get<{
        success: boolean;
        data: OrthodontistListItem[];
        meta: PaginatedMeta;
    }>('/orthodontist', { params: query });
    return { data: response.data.data, meta: response.data.meta };
};

export interface OrthodontistDetailInfo {
    id: string;
    name: string;
    drFirstName: string;
    drLastName: string;
    drEmail: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    addressDetails: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface OrthodontistPracticeSummary {
    id: string;
    name: string;
    drFirstName: string;
    drLastName: string;
    phone: string | null;
    email: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
}

export interface OrthodontistDetail {
    orthodontist: OrthodontistDetailInfo;
    practices: OrthodontistPracticeSummary[];
}

export const fetchOrthodontistDetail = async (
    orthodontistId: string
): Promise<OrthodontistDetail> => {
    const response = await apiV2.get<{ success: boolean; data: OrthodontistDetail }>(
        `/orthodontist/${orthodontistId}`
    );
    return response.data.data;
};

export type OrthodontistMembershipType = 'member' | 'odo_impersonator';

export interface OrthodontistUserRow {
    id: string;
    userId: string;
    name: string;
    email: string;
    membershipType: OrthodontistMembershipType;
    createdAt: string;
}

export const fetchOrthodontistUsers = async (
    orthodontistId: string
): Promise<OrthodontistUserRow[]> => {
    const response = await apiV2.get<{ success: boolean; data: OrthodontistUserRow[] }>(
        `/orthodontist/${orthodontistId}/users`
    );
    return response.data.data;
};

// Matches POST /orthodontist's createOrthodontistBody validator exactly -
// name/drFirstName/drLastName required, the rest optional.
export interface CreateOrthodontistBody {
    name: string;
    drFirstName: string;
    drLastName: string;
    drEmail?: string;
    phone?: string;
    email?: string;
    address?: string;
    addressDetails?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
}

// Returns the same shape as the detail endpoint's `orthodontist` field.
export const createOrthodontist = async (
    body: CreateOrthodontistBody
): Promise<OrthodontistDetailInfo> => {
    const response = await apiV2.post<{ success: boolean; data: OrthodontistDetailInfo }>(
        '/orthodontist',
        body
    );
    return response.data.data;
};

type OrthodontistRequiredKey = 'name' | 'drFirstName' | 'drLastName';

// PATCH /orthodontist/:id takes any non-empty subset; null clears a nullable field.
export type UpdateOrthodontistBody = Partial<
    Pick<CreateOrthodontistBody, OrthodontistRequiredKey>
> & {
    [K in Exclude<keyof CreateOrthodontistBody, OrthodontistRequiredKey>]?: string | null;
};

export const updateOrthodontist = async (
    orthodontistId: string,
    body: UpdateOrthodontistBody
): Promise<OrthodontistDetailInfo> => {
    const response = await apiV2.patch<{ success: boolean; data: OrthodontistDetailInfo }>(
        `/orthodontist/${orthodontistId}`,
        body
    );
    return response.data.data;
};

// Real join-table membership (orthodontist_member), not the fragile
// users.practices array - add/remove hit their own dedicated endpoints.
export const addOrthodontistUser = async (
    orthodontistId: string,
    userId: string
): Promise<OrthodontistUserRow> => {
    const response = await apiV2.post<{ success: boolean; data: OrthodontistUserRow }>(
        `/orthodontist/${orthodontistId}/users`,
        { userId }
    );
    return response.data.data;
};

export const removeOrthodontistUser = async (
    orthodontistId: string,
    userId: string
): Promise<void> => {
    await apiV2.delete(`/orthodontist/${orthodontistId}/users/${userId}`);
};

// Real join-table link (orthodontist_practice), unique on practiceId - a
// practice belongs to at most one org, enforced 409 on a second link attempt.
export const addOrthodontistPractice = async (
    orthodontistId: string,
    practiceId: string
): Promise<OrthodontistPracticeSummary> => {
    const response = await apiV2.post<{ success: boolean; data: OrthodontistPracticeSummary }>(
        `/orthodontist/${orthodontistId}/practices`,
        { practiceId }
    );
    return response.data.data;
};

export const removeOrthodontistPractice = async (
    orthodontistId: string,
    practiceId: string
): Promise<void> => {
    await apiV2.delete(`/orthodontist/${orthodontistId}/practices/${practiceId}`);
};

export interface OrthodontistMembership {
    id: string;
    name: string;
    drFirstName: string;
    drLastName: string;
}

// Backs OrthodontistMultiSelect's initial selected list - the orgs a user
// already belongs to. [] if none, never 404 unless the userId itself is gone.
export const fetchOrthodontistMembershipsByUser = async (
    userId: string
): Promise<OrthodontistMembership[]> => {
    const response = await apiV2.get<{ success: boolean; data: OrthodontistMembership[] }>(
        `/orthodontist/by-user/${userId}`
    );
    return response.data.data;
};
